import { Injectable } from '@angular/core';
// import { MatSnackBar } from '@angular/material/snack-bar'; // only used by the disabled promptUpdate()
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { interval } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Auto-update service. Keeps every open client — a browser tab AND the
 * installed standalone PWA — on the latest deploy without a manual refresh.
 *
 * One cheap check, polled every 15 minutes (and when the tab becomes visible
 * again, at most once a minute):
 *  - version.json: a plain cache-busted fetch of the deployed version file
 *    (~70 bytes). It is the universal signal — it fires even when the SW path
 *    doesn't (kill-switch off, SW unsupported, or a client the SW hasn't
 *    activated), so the browser web app and the installed PWA both update.
 *  - Only when version.json changes do we ask the service worker to
 *    checkForUpdate() (which downloads ngsw.json and the new build). Once it
 *    has cached the build it emits VERSION_READY, which we activate and reload
 *    on, so the reload serves fresh assets. The SW also checks by itself on
 *    every app start.
 *
 * The poll used to run every 60s and hit both ngsw.json and version.json each
 * time — ~92k requests/day from idle tablets (73% of all hosting requests on
 * 2026-10-07). Deploys are off hours, so a 15-minute pickup is plenty.
 *
 * NOTE: the poller is intentionally NOT gated on ApplicationRef.isStable.
 * This app keeps long-lived async work (websocket / mqtt / polling) running
 * in the Angular zone, so isStable never emits `true`; a stability-gated
 * poller would silently never start and the app would only update on a
 * manual refresh.
 *
 * AUTO-RELOAD: both triggers reload immediately, with no user prompt.
 * Deploys go out during off hours (~99% of the time), so there is no
 * in-progress POS order to protect and no reason to wait for a click.
 *
 * The previous "Update Now" snackbar path is commented out below rather than
 * deleted — restore it if deploys ever move into business hours.
 */
@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  private currentVersion: string | null = null;
  private reloading = false;
  // private updatePending = false; // only used by the disabled promptUpdate()
  private lastPollAt = 0;
  private readonly POLL_INTERVAL_MS = 15 * 60_000;
  private readonly MIN_VISIBLE_POLL_GAP_MS = 60_000;
  private readonly ACTIVATE_TIMEOUT_MS = 5_000;
  // How long to let the SW download a new build before reloading anyway.
  private readonly SW_DOWNLOAD_TIMEOUT_MS = 30_000;

  constructor(
    private readonly swUpdate: SwUpdate,
    // private readonly snackBar: MatSnackBar, // only used by the disabled promptUpdate()
  ) {}

  /** Call once at app startup (from AppComponent.ngOnInit). */
  public init(): void {
    // The installed Capacitor app updates through the app store, not the PWA
    // service worker — skip update polling entirely on native.
    if (Capacitor.isNativePlatform()) {
      return;
    }
    this.listenForVersionReady();
    this.poll(); // first check at startup
    interval(this.POLL_INTERVAL_MS).subscribe(() => this.poll());

    // Re-check immediately when a background tab becomes visible again.
    // Browsers throttle/suspend setInterval in inactive tabs, so the poller
    // may not have run for a while. This ensures the app updates as soon as
    // the user switches back to the tab. Throttled so rapid tab switching
    // doesn't turn back into a request storm.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !this.reloading
          && Date.now() - this.lastPollAt >= this.MIN_VISIBLE_POLL_GAP_MS) {
        this.poll();
      }
    });
  }

  // ── PRIMARY: SW reports a new version is cached and ready ──────────────────
  private listenForVersionReady(): void {
    if (!this.swUpdate.isEnabled) {
      return; // SW disabled (kill-switch) — the version.json check handles it
    }
    this.swUpdate.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => this.reload());
  }

  // ── Poll version.json on a fixed interval ─────────────────────────────────
  private poll(): void {
    this.lastPollAt = Date.now();
    this.checkVersionJson();
  }

  private checkVersionJson(): void {
    // ?v= cache-buster + no-store: version.json is in the lazy asset group, so
    // without a unique URL the SW would serve a stale cached copy.
    fetch(`/assets/version.json?v=${Date.now()}`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((data: { version: string }) => {
        if (this.currentVersion && this.currentVersion !== data.version) {
          this.onNewVersion();
        }
        this.currentVersion = data.version;
      })
      .catch(() => {
        /* offline/transient — ignore, retry next poll */
      });
  }

  // ── A deploy was detected: let the SW fetch it, then reload ───────────────
  private onNewVersion(): void {
    if (this.reloading) {
      return;
    }
    if (!this.swUpdate.isEnabled) {
      this.reload();
      return;
    }
    // Drives the SW toward VERSION_READY (which reloads). If that never comes —
    // SW already up to date, or the download stalls — reload anyway.
    this.swUpdate.checkForUpdate().catch(() => {
      /* transient network — the fallback reload below still runs */
    });
    setTimeout(() => this.reload(), this.SW_DOWNLOAD_TIMEOUT_MS);
  }

  // ── Show a persistent snackbar instead of force-reloading ─────────────────
  // DISABLED: deploys happen off hours, so we reload immediately instead of
  // asking. Re-point the two triggers above at promptUpdate() to restore this
  // (also uncomment `updatePending` and the MatSnackBar import/injection).
  // private promptUpdate(): void {
  //   if (this.updatePending || this.reloading) {
  //     return; // snackbar already showing or reload in progress
  //   }
  //   this.updatePending = true;
  //
  //   this.snackBar.open('A new version is available.', 'Update Now', {
  //     verticalPosition: 'top',
  //     horizontalPosition: 'center',
  //     // No duration — stays until user clicks "Update Now"
  //   }).onAction().subscribe(() => {
  //     this.reload();
  //   });
  // }

  // ── Activate any ready SW update, then hard-reload (guarded) ──────────────
  private reload(): void {
    if (this.reloading) {
      return;
    }
    this.reloading = true;

    const doReload = () => document.location.reload();

    if (this.swUpdate.isEnabled) {
      // activateUpdate() swaps the SW to the freshly-cached version so the
      // reload serves new assets. It can hang if called before the SW has an
      // update ready (the version.json check can detect a deploy a beat before
      // the SW finishes caching it), so cap it with a timeout and reload
      // regardless — the SW path then heals to the new shell on the next poll.
      let fired = false;
      const once = () => {
        if (!fired) {
          fired = true;
          doReload();
        }
      };
      this.swUpdate.activateUpdate().then(once).catch(once);
      setTimeout(once, this.ACTIVATE_TIMEOUT_MS);
    } else {
      doReload();
    }
  }
}
